import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TripCard = ({ trip, onEdit, onShare, onDelete }) => {
  const navigate = useNavigate();
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysUntil = (startDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const diffTime = start - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntil = getDaysUntil(trip?.startDate);

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-lg transition-all duration-250 group">
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
        <Image
          src={trip?.image}
          alt={trip?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 right-3 flex gap-2">
          {trip?.status === 'upcoming' && (
            <span className="px-3 py-1 bg-success text-success-foreground text-xs font-semibold rounded-full">
              Upcoming
            </span>
          )}
          {trip?.status === 'completed' && (
            <span className="px-3 py-1 bg-muted text-muted-foreground text-xs font-semibold rounded-full">
              Completed
            </span>
          )}
        </div>
        {daysUntil > 0 && daysUntil <= 30 && (
          <div className="absolute bottom-3 left-3 bg-background/90 backdrop-blur-sm px-3 py-2 rounded-lg">
            <p className="text-xs text-muted-foreground">Starts in</p>
            <p className="text-lg font-bold text-primary">{daysUntil} days</p>
          </div>
        )}
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-1 line-clamp-1">
              {trip?.destination}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-1">{trip?.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Calendar" size={16} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Start Date</p>
              <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                {formatDate(trip?.startDate)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Icon name="Clock" size={16} color="var(--color-secondary)" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Duration</p>
              <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                {trip?.duration}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
          <Icon name="Users" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {trip?.travelers} {trip?.travelers === 1 ? 'Traveler' : 'Travelers'}
          </span>
          <span className="text-muted-foreground">•</span>
          <Icon name="MapPin" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {trip?.destinations} {trip?.destinations === 1 ? 'Stop' : 'Stops'}
          </span>
        </div>

        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            fullWidth
            onClick={() => navigate(`/itinerary/${trip?.id}`)}
            iconName="Edit"
            iconPosition="left"
          >
            Edit Trip
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onShare(trip?.id)}
            iconName="Share2"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(trip?.id)}
            iconName="Trash2"
          />
        </div>
      </div>
    </div>
  );
};

export default TripCard;